import { DuctType } from "@eisox/api/core-api";
import { cn } from "@eisox/design-system";
import { WindIcon } from "@eisox/icons";

import { m } from "~/paraglide/messages";

import { getSetpointLines } from "../helpers";
import { Direction, Position, type Component } from "../types";
import { AirFlow } from "./air-flow";
import { Card } from "./card";
import { TemperatureSensor } from "./temperature-sensor";

/**
 * The setpoint tile stacks one line per setpoint shown -- heating and cooling, each possibly with
 * its computed value underneath -- and the duct name must keep its room below it.
 */
const HEIGHT_BY_SETPOINT_LINES = ["h-[207px]", "h-[207px]", "h-[237px]", "h-[267px]", "h-[297px]"];

interface OutletProps extends Component {
  buildingId: string;
  airHandlerUnitId: string;
  type: DuctType.RETURN | DuctType.SUPPLY;
  temperature?: number;
  temperatureDefect?: boolean;
  setpoint?: number;
  actualSetpoint?: number;
  coolingSetpoint?: number;
  coolingActualSetpoint?: number;
  /** Drawn between the temperature probe and the duct mouth, where the air last passes. */
  filter?: React.ReactNode;
}

const Outlet: React.FC<OutletProps> = ({
  buildingId,
  airHandlerUnitId,
  type,
  temperature,
  temperatureDefect,
  setpoint,
  actualSetpoint,
  coolingSetpoint,
  coolingActualSetpoint,
  filter,
  position = Position.TOP,
}) => {
  const setpointLines =
    getSetpointLines(actualSetpoint, setpoint).length + getSetpointLines(coolingActualSetpoint, coolingSetpoint).length;

  return (
    <div
      className={cn(
        "gap-2.5 relative flex flex-row",
        HEIGHT_BY_SETPOINT_LINES[setpointLines] ?? HEIGHT_BY_SETPOINT_LINES[HEIGHT_BY_SETPOINT_LINES.length - 1],
        position === Position.TOP ? "items-end" : "items-start",
        "after:h-23.5 after:absolute after:-z-2 after:w-full after:border-y after:border-primary-dark-gray after:bg-neutral-100 after:content-['']",
        position === Position.TOP ? "after:-bottom-px" : "after:-top-px",
      )}
    >
      {temperature !== undefined && (
        <div className="w-27.5 relative flex h-full flex-col items-center">
          <TemperatureSensor
            className={position === Position.TOP ? "bottom-0" : "top-0"}
            buildingId={buildingId}
            airHandlerUnitId={airHandlerUnitId}
            position={position}
            temperature={temperature}
            defect={temperatureDefect}
            setpoint={setpoint}
            actualSetpoint={actualSetpoint}
            coolingSetpoint={coolingSetpoint}
            coolingActualSetpoint={coolingActualSetpoint}
          />
        </div>
      )}
      {filter}
      <div className="w-27.5 relative flex h-full flex-col items-center">
        <AirFlow
          className={cn("absolute", position === Position.TOP ? "bottom-2.5" : "top-2.5")}
          direction={type === DuctType.RETURN ? Direction.LEFT : Direction.RIGHT}
        />
        <Card className={cn("absolute cursor-default", position === Position.BOTTOM && "bottom-0")}>
          <WindIcon />
          {m[type]()}
        </Card>
      </div>
    </div>
  );
};

Outlet.displayName = "Outlet";

export { Outlet };
export type { OutletProps };
